import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  GET_CONCIERGE_BROADCAST_GROUPS,
  GET_REFERRAL_BROADCAST_GROUP,
  GET_REFERRAL_LEAD,
} from "../../../../platform/graphql/queries/care";
import { parseExtra } from "./conciergeBoardShared";

function useQueryParams() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function parseSeed(seedJson: any): Record<string, any> {
  if (!seedJson) return {};
  if (typeof seedJson === "object") return seedJson;
  try {
    return JSON.parse(String(seedJson));
  } catch {
    return {};
  }
}

function getSummary(raw: any, extraObj: Record<string, any>) {
  const attribution = raw?.attributionSummary || {};
  const compensation = raw?.compensationSummary || {};
  return {
    leadSourceType: String(
      attribution?.leadSourceType ||
        raw?.leadSourceType ||
        extraObj?.leadSource?.type ||
        "PLATFORM_DIRECT",
    )
      .trim()
      .toUpperCase(),
    sourceAgentName:
      String(
        attribution?.sourceAgentName ||
          raw?.sourceAgentName ||
          extraObj?.leadSource?.sourceAgentName ||
          "",
      ).trim() || null,
    assignedAgentName:
      String(attribution?.assignedAgentName || raw?.assignedAgentName || "").trim() || null,
    commissionAgentName:
      String(
        compensation?.recipientAgentName ||
          attribution?.commissionAgentName ||
          raw?.commissionAgentName ||
          "",
      ).trim() || null,
    compensationPercent:
      compensation?.compensationPercent == null
        ? null
        : Number(compensation.compensationPercent),
    compensationNotes: String(compensation?.compensationNotes || "").trim() || null,
    compensationLockedAt: compensation?.compensationLockedAt || null,
    compensationSourceType: String(
      compensation?.compensationSourceType || "PENDING_ASSIGNMENT",
    )
      .trim()
      .toUpperCase(),
    agreementLabel:
      String(compensation?.agreementLabel || "Agent Compensation Agreement").trim(),
    agentAgreementVersion:
      String(compensation?.agentAgreementVersion || "").trim() || null,
    routingMode: String(attribution?.routingMode || raw?.routingMode || "").trim().toUpperCase(),
    assignmentOrigin: String(
      attribution?.assignmentOrigin || raw?.assignmentOrigin || "",
    )
      .trim()
      .toUpperCase(),
  };
}

function getSourceLabel(value: string) {
  return value === "AGENT_SOURCED" ? "Agent-sourced lead" : "Plany platform lead";
}

function getCompLabel(value: string) {
  if (value === "AGENT_SOURCED") return "Agent-sourced payout";
  if (value === "PLATFORM_ASSIGNED") return "Plany-assigned payout";
  return "Pending assignment";
}

function getRoutingLabel(routingMode: string, assignmentOrigin: string) {
  if (assignmentOrigin === "AGENT_CREATED") {
    return "Direct to source agent";
  }
  if (routingMode === "CLAIM_POOL" && assignmentOrigin === "SELF_CLAIMED") {
    return "Claimed from pool";
  }
  if (routingMode === "CLAIM_POOL") {
    return "Claim pool workflow";
  }
  if (assignmentOrigin === "ADMIN_ASSIGNED") {
    return "Assigned by Plany";
  }
  if (assignmentOrigin === "REASSIGNED") {
    return "Reassigned by admin";
  }
  if (assignmentOrigin === "TRANSFERRED") {
    return "Transferred between agents";
  }
  return "Assigned workflow";
}

function getOriginLabel(value: string) {
  if (value === "AGENT_CREATED") return "Agent-created";
  if (value === "SELF_CLAIMED") return "Self-claimed";
  if (value === "ADMIN_ASSIGNED") return "Admin-assigned";
  if (value === "REASSIGNED") return "Admin-reassigned";
  if (value === "TRANSFERRED") return "Transferred";
  if (value === "UNASSIGNED") return "Unassigned";
  return value || "N/A";
}

function formatDateTime(value: any) {
  if (!value) return "Not locked yet";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "Not locked yet";
  return dt.toLocaleString();
}

export default function AgentCompAgreementPage() {
  const navigate = useNavigate();
  const params = useQueryParams();
  const leadId = String(params.get("lead") || "").trim() || null;
  const groupId = String(params.get("group") || "").trim() || null;

  const leadQuery = useQuery(GET_REFERRAL_LEAD, {
    variables: { id: leadId },
    skip: !leadId,
    fetchPolicy: "cache-and-network",
  });
  const singleGroupQuery = useQuery(GET_REFERRAL_BROADCAST_GROUP, {
    variables: { id: groupId },
    skip: !groupId,
    fetchPolicy: "cache-and-network",
  });
  const listGroupQuery = useQuery(GET_CONCIERGE_BROADCAST_GROUPS, {
    variables: { limit: 300, offset: 0 },
    skip: !groupId,
    fetchPolicy: "cache-and-network",
  });

  const group = React.useMemo(() => {
    if (!groupId) return null;
    const fromSingle = singleGroupQuery.data?.referralBroadcastGroup;
    if (fromSingle?.id) return fromSingle;
    return (
      (listGroupQuery.data?.getConciergeBroadcastGroups || []).find(
        (item: any) => String(item?.id || "") === String(groupId),
      ) || null
    );
  }, [groupId, listGroupQuery.data, singleGroupQuery.data]);

  const record = leadQuery.data?.referralLead || group || null;
  const isGroup = !leadId && !!groupId;
  const seed = parseSeed(group?.seedJson);
  const extra = parseExtra(leadQuery.data?.referralLead?.extra || seed?.extra || record?.extra);
  const summary = React.useMemo(() => getSummary(record, extra), [extra, record]);

  const residentLabel = String(
    record?.residentName ||
      record?.residentKey ||
      seed?.residentKey ||
      extra?.patientKey ||
      `Case ${leadId || groupId || ""}`,
  ).trim();
  const providerName = String(record?.business?.name || "Provider to be matched").trim();
  const referrerName = String(
    record?.referrer?.contactName ||
      record?.referrerContact?.name ||
      extra?.referrerContact?.name ||
      "Referrer",
  ).trim();

  const loading = leadQuery.loading || singleGroupQuery.loading || listGroupQuery.loading;
  const error =
    leadQuery.error?.message ||
    singleGroupQuery.error?.message ||
    listGroupQuery.error?.message ||
    null;

  if (loading) return <LinearProgress sx={{ my: 2 }} />;
  if (!leadId && !groupId) {
    return <Alert severity="error">Missing `lead` or `group` parameter.</Alert>;
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }
  if (!record) {
    return <Alert severity="error">Agent agreement record not found.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: 2, py: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {summary.agreementLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Locked terms between Plany and the assigned agent for this{" "}
            {isGroup ? "screening request" : "lead"}.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button variant="contained" onClick={() => navigate("/care/agent")}>
            Agent Workspace
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          <Chip size="small" variant="outlined" label={residentLabel} />
          <Chip size="small" variant="outlined" label={getSourceLabel(summary.leadSourceType)} />
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={getCompLabel(summary.compensationSourceType)}
          />
          <Chip
            size="small"
            color={summary.compensationPercent != null ? "success" : "default"}
            variant="outlined"
            label={
              summary.compensationPercent != null
                ? `${summary.compensationPercent}% of Plany success fee`
                : "Compensation pending"
            }
          />
          {summary.agentAgreementVersion ? (
            <Chip size="small" variant="outlined" label={summary.agentAgreementVersion} />
          ) : null}
        </Stack>

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Compensation Summary
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            {summary.compensationNotes ||
              "Plany pays the assigned commission agent only after a successful placement and only from provider funds actually collected by Plany under the locked case terms below."}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Locked at: {formatDateTime(summary.compensationLockedAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Agent payouts follow actual provider collections. If the provider pays Plany in parts,
            the agent is paid in the same collected sequence, not in full upfront.
          </Typography>
        </Box>

        <Stack spacing={1.25}>
          <Typography variant="body2">
            <strong>Resident / case:</strong> {residentLabel}
          </Typography>
          <Typography variant="body2">
            <strong>Referrer:</strong> {referrerName}
          </Typography>
          <Typography variant="body2">
            <strong>Provider:</strong> {providerName}
          </Typography>
          <Typography variant="body2">
            <strong>Source of lead:</strong> {getSourceLabel(summary.leadSourceType)}
            {summary.sourceAgentName ? ` - sourced by ${summary.sourceAgentName}` : ""}
          </Typography>
          <Typography variant="body2">
            <strong>Assigned agent:</strong> {summary.assignedAgentName || "Not assigned"}
          </Typography>
          <Typography variant="body2">
            <strong>Commission recipient:</strong> {summary.commissionAgentName || "Not assigned"}
          </Typography>
          <Typography variant="body2">
            <strong>Routing:</strong> {getRoutingLabel(summary.routingMode, summary.assignmentOrigin)}
          </Typography>
          <Typography variant="body2">
            <strong>Origin:</strong> {getOriginLabel(summary.assignmentOrigin)}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Stack spacing={1.25}>
          <Typography variant="subtitle1" fontWeight={700}>
            Agreement Terms
          </Typography>
          <Typography variant="body2">
            1. This agreement is between <strong>Plany</strong> and the agent shown above, not
            between the provider and the agent directly.
          </Typography>
          <Typography variant="body2">
            2. If the lead is marked <strong>Agent-sourced</strong>, the agent receives the
            locked agent-sourced payout percentage for a successful placement.
          </Typography>
          <Typography variant="body2">
            3. If the lead is marked <strong>Plany platform lead</strong> and later assigned or
            claimed, the agent receives the locked Plany-assigned payout percentage for a
            successful placement.
          </Typography>
          <Typography variant="body2">
            4. The provider pays Plany under the provider agreement. Plany pays the agent under
            this locked agent agreement. Agents should not create side payment agreements with
            providers for this case.
          </Typography>
          <Typography variant="body2">
            5. Payment is triggered only after a successful placement or move-in that qualifies
            under Plany&apos;s placement rules and provider agreement, and only after Plany
            actually receives payment from the provider. If the provider does not pay Plany,
            no agent payment is due yet for that case.
          </Typography>
          <Typography variant="body2">
            6. If the provider agreement collects in installments, the agent commission is paid
            only from the installments Plany has actually collected. For example, if the provider
            pays 50% in the first month and the remaining balance in the next month, the agent is
            paid their proportional share from the first collection first and the remainder only
            after the second collection is received.
          </Typography>
          <Typography variant="body2">
            7. No commission is due on unpaid, reversed, refunded, charged-back, or forgiven
            provider amounts unless Plany separately approves an exception in writing.
          </Typography>
          <Typography variant="body2">
            8. If the resident moves out early, passes away, rescinds, or otherwise triggers a
            provider refund, credit, or payment reduction under the provider agreement, the agent
            commission is recalculated based on the net amount Plany actually retains for the case.
          </Typography>
          <Typography variant="body2">
            9. If Plany already paid commission on amounts later refunded or credited back to the
            provider, Plany may offset or claw back the overpaid agent amount from future payouts
            unless Plany separately approves a written exception.
          </Typography>
          <Typography variant="body2">
            10. If the case later moves off-platform after introduction, the locked compensation
            terms still follow the case if the underlying Plany provider agreement remains
            enforceable.
          </Typography>
          <Typography variant="body2">
            11. Reassignment or transfer changes ownership workflow, but the locked payout basis
            for this case stays attached to the case snapshot shown here unless Plany explicitly
            re-locks it.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
